/**
 * G13 — module-level peer `/me` coalesce + per-origin budget.
 *
 * Peer must run `/me` on login | session-refreshed (no guest-latch skip).
 * Cross-tab login must **forceFresh** so it never joins a boot `/me` that left
 * the wire before Set-Cookie landed (stale 401 → login fails to propagate).
 */

import type { RuntimeConfig } from '../../config/runtimeConfig.js';
import { fetchPlatformMe, type PlatformMeResult } from './fetchPlatformMe.js';

/** Align with fair-queue HOT_PATH — at most one peer `/me` start per 2s per origin. */
export const PEER_ME_MIN_INTERVAL_MS = 2000;

export type PeerMeRevalidateReason =
  | 'login'
  | 'session-refreshed'
  | 'visibility';

export type PeerMeRevalidateOutcome =
  | 'skipped-budget'
  | 'coalesced'
  | 'ran';

type PeerMeApply = (result: PlatformMeResult) => void;

let peerMeInFlight: Promise<PeerMeRevalidateOutcome> | null = null;
let lastPeerMeStartAt = 0;

/**
 * Coalesced peer `/me` for cross-tab login / session-refreshed / visibility catch-up.
 * Concurrent callers share one in-flight probe (budget + coalesce only).
 *
 * `login` bypasses the inter-peer budget (must not drop cookie catch-up).
 * All reasons use `forceFresh` so an in-flight boot `/me` without cookies is not reused.
 */
export async function runCoalescedPeerMeRevalidate(input: {
  readonly config: RuntimeConfig;
  readonly apply: PeerMeApply;
  readonly reason?: PeerMeRevalidateReason;
}): Promise<PeerMeRevalidateOutcome> {
  if (peerMeInFlight !== null) {
    await peerMeInFlight;
    return 'coalesced';
  }

  const reason = input.reason ?? 'session-refreshed';
  const now = Date.now();
  const bypassBudget = reason === 'login';
  if (
    !bypassBudget &&
    lastPeerMeStartAt > 0 &&
    now - lastPeerMeStartAt < PEER_ME_MIN_INTERVAL_MS
  ) {
    return 'skipped-budget';
  }

  lastPeerMeStartAt = now;
  peerMeInFlight = (async (): Promise<PeerMeRevalidateOutcome> => {
    try {
      const result = await fetchPlatformMe(input.config, undefined, {
        forceFresh: true,
      });
      input.apply(result);
      return 'ran';
    } finally {
      peerMeInFlight = null;
    }
  })();

  return peerMeInFlight;
}

/** Test-only — clear peer coalesce watermark between Jest cases. */
export function resetPeerMeRevalidateForTests(): void {
  peerMeInFlight = null;
  lastPeerMeStartAt = 0;
}
